import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, ArrowLeft, Home, AlertTriangle } from 'lucide-react';
import { useRBAC } from '../contexts/RBACContext';

interface LocationState {
  from?: string;
  requiredPermission?: string;
  requiredFeature?: string;
  requiredRole?: string;
  requiredTenantLevel?: string;
}

const UnauthorizedPage: React.FC = () => {
  const location = useLocation();
  const { rbacData } = useRBAC();
  const [missingPermission, setMissingPermission] = useState<string | null>(null);
  
  // Get the state passed from the Navigate component
  const state = location.state as LocationState | null;
  const fromPath = state?.from;
  const requiredPermission = state?.requiredPermission;
  const requiredFeature = state?.requiredFeature;
  const requiredRole = state?.requiredRole;
  const requiredTenantLevel = state?.requiredTenantLevel;
  
  // Try to determine what permission is missing based on the URL path
  useEffect(() => {
    // If we have a specific required permission from state, use that
    if (requiredPermission) {
      setMissingPermission(requiredPermission);
      return;
    }
    
    // Otherwise try to determine from the path
    const path = fromPath || location.pathname;
    let permission = null;
    
    if (path.includes('/dashboard')) {
      permission = 'ADM_VIEW_DASHBOARD';
    } else if (path.includes('/tenants') || path.includes('/partners') || path.includes('/customers')) {
      permission = path.includes('/new') ? 'ADM_CREATE_TENANTS' : 'ADM_VIEW_TENANTS';
    } else if (path.includes('/users')) {
      permission = path.includes('/new') ? 'ADM_CREATE_USERS' : 'ADM_VIEW_USERS';
    } else if (path.includes('/products')) {
      permission = 'ADM_VIEW_PRODUCTS';
    } else if (path.includes('/integrations')) {
      permission = 'ADM_VIEW_INTEGRATIONS';
    } else if (path.includes('/rbac')) {
      permission = 'ADM_VIEW_ACCESS_CONTROL';
    } else if (path.includes('/audit-logs')) {
      permission = 'ADM_VIEW_AUDIT_LOGS';
    } else if (path.includes('/forwarding')) {
      permission = 'ADM_VIEW_FORWARDING';
    }
    
    setMissingPermission(permission);
  }, [location.pathname, fromPath, requiredPermission]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-lg w-full text-center">
        {/* Icon */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-500/10 border border-red-500/20 rounded-full">
            <Shield className="h-10 w-10 text-red-500" />
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          <div>
            <h1 className="text-4xl font-bold text-text-primary mb-2">
              Access Denied
            </h1>
            <p className="text-xl text-text-secondary">
              You don't have permission to access this resource
            </p>
          </div>

          <div className="bg-background-card border border-border-dark rounded-lg p-6">
            <p className="text-text-secondary mb-4">
              This page requires specific permissions that your account doesn't currently have. 
              Please contact your administrator if you believe this is an error.
            </p>
            
            <div className="text-sm text-text-muted">
              <p><strong>Error Code:</strong> 403 - Forbidden</p>
              <p><strong>Resource:</strong> {fromPath || location.pathname}</p>
              
              {/* Show missing permission info */}
              {missingPermission && (
                <div className="mt-4 p-3 bg-amber-900/20 border border-amber-700/30 rounded-md text-left">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-500">Missing Permission</p>
                      <p className="text-text-muted mt-1">You need the <code className="bg-background-hover px-1 py-0.5 rounded text-amber-400">{missingPermission}</code> permission to access this page.</p>
                      
                      {rbacData?.permission_codes && (
                        <div className="mt-3">
                          <p className="font-medium text-sm text-text-secondary">Your current permissions:</p>
                          <div className="mt-1 max-h-24 overflow-y-auto">
                            {rbacData.permission_codes.length > 0 ? (
                              <ul className="list-disc list-inside text-xs text-text-muted space-y-1">
                                {rbacData.permission_codes.map((perm, idx) => (
                                  <li key={idx}>{perm}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs text-text-muted italic">No permissions assigned</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Show required feature info */}
              {requiredFeature && (
                <div className="mt-4 p-3 bg-amber-900/20 border border-amber-700/30 rounded-md text-left">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-500">Missing Feature Access</p>
                      <p className="text-text-muted mt-1">You need access to the <code className="bg-background-hover px-1 py-0.5 rounded text-amber-400">{requiredFeature}</code> feature to access this page.</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Show required role info */}
              {requiredRole && (
                <div className="mt-4 p-3 bg-amber-900/20 border border-amber-700/30 rounded-md text-left">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-500">Insufficient Role</p>
                      <p className="text-text-muted mt-1">You need one of these roles: <code className="bg-background-hover px-1 py-0.5 rounded text-amber-400">{requiredRole}</code> to access this page.</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Show required tenant level info */}
              {requiredTenantLevel && (
                <div className="mt-4 p-3 bg-amber-900/20 border border-amber-700/30 rounded-md text-left">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-500">Insufficient Tenant Level</p>
                      <p className="text-text-muted mt-1">You need one of these tenant levels: <code className="bg-background-hover px-1 py-0.5 rounded text-amber-400">{requiredTenantLevel}</code> to access this page.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Home className="h-4 w-4 mr-2" />
              Go to Dashboard
            </Link>
            
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center justify-center px-6 py-3 bg-background-hover text-text-primary border border-border-dark rounded-lg hover:bg-background-card transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </button>
          </div>

          {/* Additional Help */}
          <div className="pt-6 border-t border-border-dark">
            <p className="text-sm text-text-muted">
              Need help? Contact your system administrator or{' '}
              <a 
                href="mailto:support@eventussecurity.com" 
                className="text-primary hover:text-primary/80 underline"
              >
                support@eventussecurity.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnauthorizedPage;